import { useHistory, useQueryParams } from '@fs/zion-router'
import { useCallback } from 'react'
import { useSlsStateContext, useSlsDispatchContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import useGetThumbnail from './useGetThumbnail'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

/**
 * useSetFocusedIndex - Use this hook when you need to change the focused index
 * @returns function that will set the focused index and update the image ark in the url and internal state
 */
export default function useSetFocusedIndex(doSwitchImageCheck = true) {
  const { query } = useQueryParams({ replaceState: true })
  const { view } = query
  const history = useHistory()

  const overlayContext = useOverlayContext()
  const { switchImageCheck } = overlayContext
  const sharedBrowserState = useSharedBrowserStateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()

  const getThumbnail = useGetThumbnail()

  const setFocusedIndex = useCallback(
    (index) => {
      // All panels have to save first before we can move to the new image, unless this is the first time the index is set
      if (!doSwitchImageCheck || switchImageCheck()) {
        sharedBrowserDispatch({ type: 'SET_FOCUSED_INDEX', value: index })
      }
    },
    [sharedBrowserDispatch, switchImageCheck, doSwitchImageCheck]
  )

  /**
   * Handle updating the image ark in the url. This needs to change everytime an image is selected or navigated to.
   * This is important for feedback and for sharing urls.
   */
  const handleImageArkInUrl = useCallback(
    (newArk) => {
      const search = window.location.search
      const searchWithOutPersonArk = search
        ?.split('&')
        .filter((x) => !x.includes('personArk'))
        .join('&')

      if (view === 'explore') {
        history.replace({
          pathname: `/ark:/61903/${newArk}`,
          search: searchWithOutPersonArk,
        })
      } else {
        history.push({
          pathname: `/ark:/61903/${newArk}`,
          search: searchWithOutPersonArk,
        })
      }
    },
    [history, view]
  )

  const setFocusIndex = useCallback(
    (idx, _, newGroupIndex) => {
      if (idx === sharedBrowserState.focusedIndex && typeof newGroupIndex !== 'number') return

      const { ark } = getThumbnail(idx, newGroupIndex)
      if (ark && (ark !== zionSlsState.currentImageArk || sharedBrowserState.focusedIndex < 0)) {
        setFocusedIndex(idx)
        zionSlsDispatch({ type: 'SET_DATA', value: { currentImageArk: ark } })
        handleImageArkInUrl(ark)
      }
    },
    [
      handleImageArkInUrl,
      setFocusedIndex,
      sharedBrowserState.focusedIndex,
      getThumbnail,
      zionSlsState.currentImageArk,
      zionSlsDispatch,
    ]
  )

  return setFocusIndex
}
